import React, { useMemo, useState } from "react";
import { HiOutlineClipboardCopy, HiOutlineDownload } from "react-icons/hi";
import { convertEpubToMarkdown } from "../services/api";

const sanitizeFilename = (name) =>
  (name || "converted-book")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const ConvertEpub = () => {
  const [file, setFile] = useState(null);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const suggestedFilename = useMemo(() => {
    if (!result?.title) {
      return "converted-book.md";
    }
    return `${sanitizeFilename(result.title)}.md`;
  }, [result?.title]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) {
      setFile(null);
      return;
    }

    if (!selectedFile.name.toLowerCase().endsWith(".epub")) {
      setFile(null);
      setError("Please select a valid EPUB file.");
      return;
    }

    setFile(selectedFile);
    setError("");
  };

  const handleConvert = async (event) => {
    event.preventDefault();
    if (!file) {
      setError("Please select an EPUB file.");
      return;
    }

    setConverting(true);
    setError("");
    setResult(null);
    setCopied(false);

    try {
      const response = await convertEpubToMarkdown(file);
      setResult(response);
    } catch (conversionError) {
      setError(conversionError.message || "Conversion failed.");
    } finally {
      setConverting(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.markdown) {
      return;
    }

    try {
      await navigator.clipboard.writeText(result.markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      setError("Failed to copy Markdown to clipboard.");
    }
  };

  const handleDownload = () => {
    if (!result?.markdown) {
      return;
    }

    const blob = new Blob([result.markdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = suggestedFilename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: "var(--foreground)" }}>
            EPUB to Markdown Converter
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--foreground-muted)" }}>
            Convert an EPUB into Markdown with full metadata and table of contents at the top.
          </p>
        </div>

        <div className="surface-card rounded-2xl p-6 mb-6">
          <form onSubmit={handleConvert} className="space-y-4">
            <div>
              <label htmlFor="epub-converter-file" className="block text-sm font-semibold mb-2" style={{ color: "var(--foreground)" }}>
                EPUB File
              </label>
              <input
                id="epub-converter-file"
                type="file"
                accept=".epub"
                onChange={handleFileChange}
                disabled={converting}
                className="field block w-full rounded-lg text-sm px-3 py-2 file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[var(--accent-tint)] file:text-[var(--accent)]"
              />
              {file && (
                <p className="mt-2 text-sm" style={{ color: "var(--foreground-muted)" }}>
                  Selected: {file.name}
                </p>
              )}
            </div>

            {error && (
              <div className="notice-error rounded-lg p-4 text-sm">{error}</div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!file || converting}
                className="button-primary px-5 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {converting ? (
                  <>
                    <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: "var(--accent-foreground)", borderTopColor: "transparent" }} />
                    Converting...
                  </>
                ) : (
                  "Convert EPUB"
                )}
              </button>
            </div>
          </form>
        </div>

        {result && (
          <div className="surface-card rounded-2xl p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <div>
                <p className="text-sm" style={{ color: "var(--foreground-muted)" }}>
                  Title: <span className="font-medium" style={{ color: "var(--foreground)" }}>{result.title || "Untitled Book"}</span>
                </p>
                <p className="text-sm" style={{ color: "var(--foreground-muted)" }}>
                  Words: <span className="font-medium" style={{ color: "var(--foreground)" }}>{(result.word_count || 0).toLocaleString()}</span>
                  {" · "}
                  TOC Items: <span className="font-medium" style={{ color: "var(--foreground)" }}>{result.toc_items || 0}</span>
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="button-secondary px-3 py-2 text-sm font-medium rounded-lg flex items-center gap-2"
                >
                  <HiOutlineClipboardCopy className="w-4 h-4" aria-hidden="true" />
                  {copied ? "Copied" : "Copy Markdown"}
                </button>
                <button
                  type="button"
                  onClick={handleDownload}
                  className="button-primary px-3 py-2 text-sm font-medium rounded-lg flex items-center gap-2"
                >
                  <HiOutlineDownload className="w-4 h-4" aria-hidden="true" />
                  Download .md
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="markdown-output" className="block text-sm font-semibold mb-2" style={{ color: "var(--foreground)" }}>
                Markdown Output
              </label>
              <textarea
                id="markdown-output"
                value={result.markdown || ""}
                readOnly
                className="field w-full h-[28rem] p-3 rounded-lg text-sm font-mono"
                style={{ fontFamily: '"Cascadia Mono", "SFMono-Regular", Consolas, monospace' }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConvertEpub;
